// Regras puras da tela Plano de ação (sem antd, para rodar no jest do CRA).
// Aqui só se decide o que MOSTRAR (botão, cor, contagem). Quem barra de verdade
// é o servidor (`gestao.plano_acao.*` + dono da tarefa/etapa).
import { dataAoMeioDia, dataIso, hojeIso, somarDiasIso } from '../../utils/dataFmt';
import type { Etapa, Informacao, Permissoes, StatusEtapa, StatusInfo, StatusTarefa, Tarefa } from './api';

export { hojeIso };

/** Datas "AAAA-MM-DD" (ou ISO do banco) → "AAAA-MM-DD" sem deslocar por fuso. */
export const dia = (s: string | null | undefined): string | null => dataIso(s);

export type SituacaoDoCiclo = 'antes' | 'em_andamento' | 'depois' | 'sem_etapas';
export interface SemanaAtual { situacao: SituacaoDoCiclo; numero: number | null; total: number }

/**
 * Semana do ciclo pela data de hoje. A semana vale do início dela até o início
 * da seguinte (o fim de semana ainda é "a semana que passou"). Antes do início
 * do ciclo: `antes`; depois do fim da última: `depois`.
 */
export function semanaAtual(etapas: Pick<Etapa, 'numero' | 'inicio' | 'fim'>[], hoje: string): SemanaAtual {
  const lista = etapas.map((e) => ({ numero: e.numero, ini: dia(e.inicio), fim: dia(e.fim) }))
    .filter((e): e is { numero: number; ini: string; fim: string | null } => !!e.ini)
    .sort((a, b) => a.numero - b.numero);
  const total = lista.length;
  if (!total) return { situacao: 'sem_etapas', numero: null, total: 0 };
  if (hoje < lista[0].ini) return { situacao: 'antes', numero: null, total };
  const ultima = lista[total - 1];
  // Igual ao servidor: o fim de semana depois da última sexta ainda é "a semana que passou";
  // o ciclo só termina na segunda seguinte.
  if (ultima.fim && hoje > ultima.fim) {
    const dow = dataAoMeioDia(ultima.fim)?.getDay() ?? 5;
    const segunda = somarDiasIso(ultima.fim, ((8 - dow) % 7) || 7);
    if (!segunda || hoje >= segunda) return { situacao: 'depois', numero: null, total };
  }
  let atual = lista[0];
  for (const e of lista) if (e.ini <= hoje) atual = e;
  return { situacao: 'em_andamento', numero: atual.numero, total };
}

export function fraseDaSemana(s: SemanaAtual): string {
  if (s.situacao === 'sem_etapas') return 'Sem semanas cadastradas';
  if (s.situacao === 'antes') return 'O ciclo ainda não começou';
  if (s.situacao === 'depois') return 'Ciclo terminado';
  return `Semana ${s.numero} de ${s.total}`;
}

const ENCERRADA: StatusTarefa[] = ['concluida', 'cancelada'];

/**
 * Atrasada = tem prazo anterior a hoje e ainda não foi concluída nem cancelada.
 * Sem status (ciclo ainda não ligado, ou atividade apagada) não há tarefa de
 * ninguém para estar atrasada — igual ao servidor.
 */
export function tarefaAtrasada(t: Pick<Tarefa, 'prazo' | 'status'>, hoje: string): boolean {
  const p = dia(t.prazo);
  if (!p || !t.status) return false;
  if (ENCERRADA.includes(t.status)) return false;
  return p < hoje;
}

/** Cancelada não conta (nem como feita, nem como pendente). */
export function tarefasQueContam<T extends Pick<Tarefa, 'status'>>(tarefas: T[]): T[] {
  return tarefas.filter((t) => t.status !== 'cancelada');
}

export function progresso(tarefas: Pick<Tarefa, 'status'>[]): { feitas: number; total: number; pct: number } {
  const v = tarefasQueContam(tarefas);
  const feitas = v.filter((t) => t.status === 'concluida').length;
  return { feitas, total: v.length, pct: v.length ? Math.round((feitas / v.length) * 100) : 0 };
}

export interface Resumo { pct: number; feitas: number; total: number; atrasadas: number; travadas: number }
export function resumoDoCiclo(etapas: Etapa[], hoje: string): Resumo {
  const todas = etapas.flatMap((e) => e.tarefas);
  const p = progresso(todas);
  return {
    pct: p.pct, feitas: p.feitas, total: p.total,
    atrasadas: todas.filter((t) => tarefaAtrasada(t, hoje)).length,
    travadas: etapas.filter((e) => e.status === 'travada').length,
  };
}

/** Quantas tarefas serão criadas em "Minhas atividades" ao ligar (as que ainda não têm atividade). */
export function tarefasParaLigar(etapas: Pick<Etapa, 'tarefas'>[]): number {
  return etapas.reduce((n, e) => n + e.tarefas.filter((t) => !t.atividade_id).length, 0);
}

// ─── Quem pode o quê (só para esconder botão; o servidor é que barra) ───
const sou = (p: Permissoes, papel: string | null | undefined) => !!papel && p.papeis_do_usuario.includes(papel);

export const podeMudarTarefa = (p: Permissoes, t: Pick<Tarefa, 'papel_responsavel' | 'atividade_id'>): boolean =>
  !!t.atividade_id && (p.pode_gerir || (p.pode_editar && sou(p, t.papel_responsavel)));
export const podeMudarInformacao = (p: Permissoes, i: Pick<Informacao, 'papel_responsavel'>): boolean =>
  p.pode_gerir || (p.pode_editar && sou(p, i.papel_responsavel));
export const podeDecidirEtapa = (p: Permissoes, e: Pick<Etapa, 'papel_dono_decisao'>): boolean =>
  p.pode_gerir || (p.pode_editar && sou(p, e.papel_dono_decisao));
export const podeGerir = (p: Permissoes): boolean => p.pode_gerir;

/** Trava exige motivo escrito. */
export function travaValida(status: StatusEtapa, motivo: string): string | null {
  if (status === 'travada' && !motivo.trim()) return 'Escreva o motivo da trava.';
  return null;
}

// ─── Rótulos (português simples) ───
export const ROTULO_ETAPA: Record<StatusEtapa, { texto: string; cor: string }> = {
  nao_iniciada: { texto: 'Não iniciada', cor: 'default' },
  em_andamento: { texto: 'Em andamento', cor: 'blue' },
  concluida: { texto: 'Concluída', cor: 'green' },
  travada: { texto: 'Travada', cor: 'red' },
};
export const ROTULO_TAREFA: Record<StatusTarefa, string> = {
  pendente: 'Pendente', em_andamento: 'Em andamento', concluida: 'Concluída', cancelada: 'Cancelada',
};
export const ROTULO_INFO: Record<StatusInfo, string> = { pendente: 'Pendente', em_andamento: 'Em andamento', pronto: 'Pronto' };
export const ROTULO_FONTE: Record<string, { texto: string; cor: string }> = {
  erp: { texto: 'ERP', cor: 'blue' },
  levantar: { texto: 'Levantar', cor: 'orange' },
  decisao: { texto: 'Decisão', cor: 'purple' },
};

/** "19/10 a 23/10", sem usar Date (não desloca por fuso). */
export function periodoCurto(ini: string | null, fim: string | null): string {
  const f = (s: string | null) => { const d = dia(s); return d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '—'; };
  return `${f(ini)} a ${f(fim)}`;
}

/** Segunda-feira da semana de `hoje` ("AAAA-MM-DD"); sugestão de início do ciclo. */
export function segundaDaSemana(hoje: string): string {
  const d = dataAoMeioDia(hoje);
  if (!d) return hoje;
  const dow = d.getDay(); // 0 = domingo
  return somarDiasIso(hoje, dow === 0 ? -6 : 1 - dow) || hoje;
}
